import { useState, useMemo } from 'react';

interface UseCarouselProps {
	totalItems: number;
	itemsPerPage?: number;
}

interface UseCarouselReturn {
	currentPage: number;
	totalPages: number;
	canGoPrev: boolean;
	canGoNext: boolean;
	direction: 'left' | 'right';
	handlePrev: () => void;
	handleNext: () => void;
	goToPage: (page: number) => void;
	getVisibleRange: () => { start: number; end: number };
}

const useCarousel = ({
	totalItems,
	itemsPerPage = 4
}: UseCarouselProps): UseCarouselReturn => {
	const [currentPage, setCurrentPage] = useState(0);
	const [direction, setDirection] = useState<'left' | 'right'>('right');

	const totalPages = useMemo(() => Math.ceil(totalItems / itemsPerPage), [
		totalItems,
		itemsPerPage
	]);

	const canGoPrev = currentPage > 0;
	const canGoNext = currentPage < totalPages - 1;

	const handlePrev = () => {
		if (canGoPrev) {
			setDirection('left');
			setCurrentPage((prev) => prev - 1);
		}
	};

	const handleNext = () => {
		if (canGoNext) {
			setDirection('right');
			setCurrentPage((prev) => prev + 1);
		}
	};

	const goToPage = (page: number) => {
		if (page >= 0 && page < totalPages) setCurrentPage(page);
	};

	const getVisibleRange = () => ({
		start: currentPage * itemsPerPage,
		end: currentPage * itemsPerPage + itemsPerPage
	});

	return {
		currentPage,
		totalPages,
		canGoPrev,
		canGoNext,
		direction,
		handlePrev,
		handleNext,
		goToPage,
		getVisibleRange
	};
};

export default useCarousel;
